import { Commune } from "../commune-interface";
import {
  IsAcceptableDistance,
  SanitizeString,
  searchHayStackUtil,
} from "./utils";
import { triggerHapticFeedback } from "../utils/hapticUtils";
import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  TextField,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import { useReset } from "../context/ResetContext";

export default function GameInputComponent({
  communes,
  setCommunes,
}: {
  communes: Commune[];
  setCommunes: React.Dispatch<React.SetStateAction<Commune[]>>;
}) {
  const [wrongGuess, setWrongGuess] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [openResetConfirm, setOpenResetConfirm] = useState(false);
  const [openAboutDialog, setOpenAboutDialog] = useState(false);
  const [openCongratulationDialog, setOpenCongratulationDialog] =
    useState(false);
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const communeHash = Object.fromEntries(
    communes.map((c) => [SanitizeString(c.name), c.code])
  );
  const communesHayStack = Object.keys(communeHash);

  const { triggerReset } = useReset();

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFormSubmit = (e: any) => {
    e.preventDefault();
    const found = searchHayStackUtil(
      communesHayStack,
      SanitizeString(inputValue)
    );
    if (!!found && IsAcceptableDistance(found, inputValue)) {
      let newCommunes = [...communes];
      const foundCommuneIdx = newCommunes.findIndex(
        (c) => c.code === communeHash[found]
      );
      if (!communes[foundCommuneIdx].timeFound!) {
        newCommunes[foundCommuneIdx].timeFound = new Date();
        setCommunes(newCommunes);
        setWrongGuess("");
        triggerHapticFeedback('success');
        if (newCommunes.every((c) => c.timeFound!)) {
          setOpenCongratulationDialog(true);
        }
      } else {
        setWrongGuess("ou ja touvé'y"); // Changed from "déjà trouvé" to créole
        triggerHapticFeedback('warning');
      }
    } else {
      setWrongGuess("pa bon"); // Changed from "faux" to créole "pa bon"
      triggerHapticFeedback('error');
    }
    e.target.reset();
  };

  const handleReset = (e: any) => {
    e.preventDefault();
    let newCommunes = [...communes];
    newCommunes.forEach((c) => (c.timeFound = null));
    setCommunes(newCommunes);
    triggerReset();
    DismissResetDialog(e);
  };

  const handleResetRequest = (e: any) => {
    setOpenResetConfirm(true);
  };

  const DismissResetDialog = (e: any) => {
    setOpenResetConfirm(false);
    setAnchorEl(null);
  };

  const handleValueChanged = (e: any) => {
    setInputValue(e.target.value);
    // Clear any error message when user types
    if (wrongGuess) {
      setWrongGuess("");
    }
  };


  const handleSupportClick = () => {
    window.open("https://www.paypal.com/paypalme/crono972", "paypalFhocs");
  };

  return (
    <Box 
      display="flex" 
      alignItems="center" 
      sx={{ 
        width: '100%',
        flexWrap: 'nowrap'
      }}
    >
      <form 
        autoComplete="off" 
        onSubmit={handleFormSubmit}
        style={{ flexGrow: 1 }}
      >
        <TextField
          fullWidth
          className="game-input"
          id="outlined-search"
          label="Komin péyi a"
          type="search"
          error={wrongGuess !== ""}
          onChange={handleValueChanged}
          helperText={wrongGuess}
          size="medium"
          variant="outlined"
          InputProps={{
            sx: {
              borderRadius: 2,
              backgroundColor: 'white',
              height: { xs: '48px', md: 'auto' },
              fontWeight: 500
            }
          }}
          sx={{
            animation: wrongGuess ? "shake 0.5s" : "none",
          }}
        />
      </form>
      <Box sx={{ ml: 1 }}>
        <IconButton 
          aria-label="options" 
          onClick={handleClick}
          sx={{
            backgroundColor: 'background.paper',
            boxShadow: 1,
            '&:hover': { backgroundColor: 'background.paper' }
          }}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <MenuList>
          <MenuItem onClick={handleResetRequest}>
            <ListItemIcon>
              <RestartAltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Recommencer</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleSupportClick}>
            <ListItemIcon>
              <VolunteerActivismIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Soutenir le projet</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => setOpenAboutDialog(true)}>
            <ListItemIcon>
              <InfoIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>A propos</ListItemText>
          </MenuItem>
        </MenuList>
      </Popover>
      <Dialog
        open={openResetConfirm}
        onClose={DismissResetDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Reset game</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Vous allez perdre toute votre progression, êtes vous sûr ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={DismissResetDialog}>
            Non
          </Button>
          <Button
            variant="contained"
            onClick={(e) => {
              handleReset(e);
              DismissResetDialog(e);
            }}
            autoFocus
          >
            Oui
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openAboutDialog}
        onClose={() => {
          setOpenAboutDialog(false);
          setAnchorEl(null);
        }}
      >
        <DialogTitle>A propos</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Site web réalisé par FHocs (frederic@hocansung.com)
          </DialogContentText>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openCongratulationDialog}
        onClose={() => setOpenCongratulationDialog(false)}
      >
        <DialogTitle>Félicitations!</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vous semblez connaitre la Martinique
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(e) => {
              handleReset(e);
              setOpenCongratulationDialog(false);
            }}
            color="primary"
          >
            Recommencer
          </Button>
          <Button
            onClick={() => setOpenCongratulationDialog(false)}
            color="primary"
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
